import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';

import { SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED } from 'api/utilityActionTypes';

import { hasRoleRenter } from 'common/utility/userRoles';

import {
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityAuthActions from 'client/actions/UtilityAuth';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';

import './SrUtilitySetupPage.less';

export const FORM_ID_PHONE = 'phone';
export const FORM_ID_VERIFICATION_CODE = 'verificationCode';

export const getFormFields = ({ formId, codeSent, onClickResendCode }) => {
  return [
    showFieldIf(FORM_ID_PHONE === formId, {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      properties: {
        placeholder: 'type your phone...',
      },
      validations: ['required', 'phone', 'sameValue:userPhone::Phone must match'],
    }),
    showFieldIf(FORM_ID_VERIFICATION_CODE === formId, {
      id: 'code',
      type: 'verification_code',
      title: 'Access Code',
      titleToggle: {
        text: codeSent ? 'Sent now!' : 'Resend code',
        onClick: onClickResendCode,
      },
      properties: {
        placeholder: 'Enter the code here...',
        autoFocus: true,
      },
      validations: ['required'],
    }),
    {
      id: 'userPhone',
      type: 'hidden',
    },
  ];
};

export const getFormTitle = ({ formId }) => {
  return formId === FORM_ID_PHONE ? (
    'Confirm your phone to see your utilities.'
  ) : (
    <>
      We just texted you a code. <br /> Enter it below:
    </>
  );
};

export const getButtonTitle = ({ formId }) => {
  return formId === FORM_ID_PHONE ? 'Text me the code' : 'Continue';
};

export const useUtilitySetupVerifyPhone = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const [formId, setFormId] = useState(FORM_ID_PHONE);
  const [phone, setPhone] = useState('');
  const [apiError, setApiError] = useState(null);
  const [codeSent, setCodeSent] = useState(false);

  const onRequestCode = (formValues) => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED,
      payload: {
        phone: formValues.phone,
        isUtilityProfit: false,
      },
      onSuccess: () => {
        setPhone(formValues.phone);
        setFormId(FORM_ID_VERIFICATION_CODE);
      },
      onError: (error) => {
        setApiError(error);
      },
    });
  };

  const onSubmit = (formValues) => {
    switch (formId) {
      case FORM_ID_PHONE: {
        onRequestCode(formValues);
        break;
      }

      case FORM_ID_VERIFICATION_CODE: {
        dispatch(UtilityAuthActions.loginSmsCode, {
          phone: phone,
          code: formValues.code,
          onSuccess: onSuccess,
          onError: (error) => {
            setApiError(error);
          },
        });
        break;
      }
    }
  };

  return {
    phone,
    formId,
    apiError,
    codeSent,
    setCodeSent,
    onRequestCode,
    onSubmit,
  };
};

export const SrUtilitySetupVerifyPhoneErrorMessage = ({ error }) => {
  return (
    <SrBox marginTop="10px" borderRadius="3px">
      <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>{error.message}</SrErrorMessage>
    </SrBox>
  );
};

const SrUtilitySetupVerifyPhone = ({ utilityUser, utilityActivation }) => {
  const history = useHistory();
  const { formId, phone, apiError, codeSent, setCodeSent, onRequestCode, onSubmit } =
    useUtilitySetupVerifyPhone({
      onSuccess: () => {
        history.push('/utility-setup/session');
      },
    });

  const canContinueFlow = !utilityUser?.id || hasRoleRenter(utilityUser?.roles || []);

  const signupLink = `/utility-setup/${utilityActivation.partner.slug}/signup/${utilityActivation.addressId}`;

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({
        formFields: getFormFields({ formId }),
        defaultValues: {
          phone: '',
          code: '',
          userPhone: utilityUser.phone,
        },
      })}
      validate={(formValues) => {
        const formFields = getFormFields({ formId });
        return createFormValidationFn(formFields)(formValues);
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          formId,
          codeSent,
          onClickResendCode: () => {
            onRequestCode({ phone });
            setCodeSent(true);
            setTimeout(() => {
              setCodeSent(false);
            }, 3000);
          },
        });
        const formTitle = getFormTitle({ formId });
        const buttonTitle = getButtonTitle({ formId });

        return (
          <Form styleName="view">
            <div styleName="titleBox">
              <span>{formTitle}</span>
              <br />
              <span styleName="subTitle">
                Use phone ending in (***) ***-{utilityUser?.phone?.slice(-4)}
              </span>
            </div>
            <div styleName="contentBox">
              <div styleName="cardBox">
                {formFields.map((field) => (
                  <SrFormikField
                    key={field.id}
                    formField={field}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    className="utilityProviderQuestion"
                  />
                ))}
                <SrBox margin="21px 0 0">
                  <SrButton
                    hasBoxShadow
                    type="submit"
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    disabled={!canContinueFlow}
                    onClick={formikBag.handleSubmit}
                  >
                    {buttonTitle}
                  </SrButton>
                  {apiError && <SrUtilitySetupVerifyPhoneErrorMessage error={apiError} />}
                  {!canContinueFlow && (
                    <SrUtilitySetupVerifyPhoneErrorMessage
                      error={{ message: 'Sorry, only renters can continue.' }}
                    />
                  )}
                </SrBox>
                <SrBox textAlign="center" margin="20px 0 0">
                  <SrLink styleName="linkText" to={signupLink}>
                    Need an account? Sign up here →
                  </SrLink>
                  <br />
                  <br />
                </SrBox>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

SrUtilitySetupVerifyPhone.propTypes = {
  utilityUser: PropTypes.object,
};

SrUtilitySetupVerifyPhone.displayName = 'SrUtilitySetupVerifyPhone';

export default SrUtilitySetupVerifyPhone;
